import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Mail, Lock, User, Building2 } from 'lucide-react'
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  type User as FirebaseUser,
} from 'firebase/auth'
import { doc, getDoc, serverTimestamp, writeBatch } from 'firebase/firestore'
import { auth, db } from '@/lib/firebase/config'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/contexts/ToastContext'
import { Button, Input, Card } from '@/components/ui'
import Logo from '@/components/shared/Logo'
import { ASILIFLOW_MONTHLY_PRICE, ASILIFLOW_PLAN_NAME } from '@/config/billing'

const signupSchema = z.object({
  agencyName: z.string().min(2, 'Agency name is required'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
})

type SignupForm = z.infer<typeof signupSchema>

function buildAgencyData(user: FirebaseUser, data: SignupForm) {
  return {
    name: data.agencyName,
    legalName: data.agencyName,
    email: data.email,
    phone: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    status: 'active' as const,
    subscription: {
      plan: 'monthly' as const,
      planName: ASILIFLOW_PLAN_NAME,
      price: ASILIFLOW_MONTHLY_PRICE,
      status: 'trial' as const,
      trialEndsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    },
    billingEmail: data.email,
    ownerUserId: user.uid,
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  }
}

function buildAgencySettingsData(user: FirebaseUser) {
  return {
    shifts: {
      dropRequiresReason: true,
      dropNoticeHoursMin: 4,
      dropRequiresApproval: false,
      pickupMode: 'request_approval',
      pickupEligibilityRules: {
        requiresActiveStatus: true,
        requiresMatchingSkills: false,
      },
      coverageAlertHoursBefore: [72, 48, 24],
    },
    authorizations: {
      expirationAlertDays: [90, 60, 30],
      utilizationAlertThresholds: [80, 90, 95],
    },
    plansOfCare: {
      reviewAlertDaysBefore: 30,
      requireSignature: true,
      requireSupervisorApproval: true,
    },
    incidents: {
      types: [
        { id: 'fall', name: 'Fall', severity: 'high', requiresImmediateReview: true, notifyRoles: ['supervisor'], isActive: true },
        { id: 'injury', name: 'Injury', severity: 'high', requiresImmediateReview: true, notifyRoles: ['supervisor'], isActive: true },
        { id: 'medication_error', name: 'Medication Error', severity: 'critical', requiresImmediateReview: true, notifyRoles: ['supervisor', 'admin'], isActive: true },
        { id: 'behavioral', name: 'Behavioral Issue', severity: 'medium', requiresImmediateReview: false, notifyRoles: ['supervisor'], isActive: true },
        { id: 'other', name: 'Other', severity: 'low', requiresImmediateReview: false, notifyRoles: ['supervisor'], isActive: true },
      ],
      requiresPhotoForTypes: ['fall', 'injury'],
      autoNotifyRoles: ['supervisor'],
    },
    visitNotes: {
      requiredFields: ['summary', 'clientCondition'],
      taskChecklistEnabled: true,
      requireSignature: false,
    },
    notifications: {
      emailEnabled: true,
      smsEnabled: false,
      visitReminderHoursBefore: 24,
      dailyDigestEnabled: false,
    },
    security: {
      sessionTimeoutMinutes: 30,
      mfaRequiredRoles: ['admin'],
      passwordMinLength: 8,
      passwordRequireSpecialChar: true,
    },
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  }
}

function buildUserData(user: FirebaseUser, data: SignupForm) {
  return {
    uid: user.uid,
    agencyId: user.uid,
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    role: 'admin' as const,
    primaryRole: 'admin' as const,
    isAgencyOwner: true,
    status: 'active' as const,
    notificationPreferences: {
      email: true,
      sms: false,
      push: true,
    },
    mfaEnabled: false,
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    isDeleted: false,
  }
}

async function ensureSignupBootstrap(user: FirebaseUser, data: SignupForm) {
  const agencyRef = doc(db, 'agencies', user.uid)
  const settingsRef = doc(db, 'agencies', user.uid, 'settings', 'config')
  const userRef = doc(db, 'users', user.uid)

  const [agencySnap, settingsSnap, userSnap] = await Promise.all([
    getDoc(agencyRef),
    getDoc(settingsRef),
    getDoc(userRef),
  ])

  const missingAgency = !agencySnap.exists()
  const missingSettings = !settingsSnap.exists()
  const missingUser = !userSnap.exists()

  if (!missingAgency && !missingSettings && !missingUser) {
    return 'already_exists' as const
  }

  const batch = writeBatch(db)

  if (missingAgency) {
    batch.set(agencyRef, buildAgencyData(user, data))
  } else {
    const agencyData = agencySnap.data()
    if (!agencyData?.ownerUserId) {
      batch.set(agencyRef, {
        ownerUserId: user.uid,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid,
      }, { merge: true })
    }
  }

  if (missingSettings) {
    batch.set(settingsRef, buildAgencySettingsData(user))
  }

  if (missingUser) {
    batch.set(userRef, buildUserData(user, data))
  } else {
    const existingUserData = userSnap.data()
    if (existingUserData?.isAgencyOwner !== true) {
      batch.set(userRef, {
        uid: user.uid,
        primaryRole: existingUserData?.primaryRole || existingUserData?.role || 'admin',
        isAgencyOwner: true,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid,
      }, { merge: true })
    }
  }

  await batch.commit()

  return missingAgency && missingSettings && missingUser
    ? ('created' as const)
    : ('recovered' as const)
}

export default function Signup() {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { signIn } = useAuth()
  const { success, info, error: showError } = useToast()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
  })

  const onSubmit = async (data: SignupForm) => {
    setIsLoading(true)
    try {
      let user: FirebaseUser
      let recoveredExistingAccount = false

      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          data.email,
          data.password
        )
        user = userCredential.user
      } catch (err) {
        const error = err as { code?: string }
        if (error.code !== 'auth/email-already-in-use') {
          throw err
        }

        const existingCredential = await signInWithEmailAndPassword(
          auth,
          data.email,
          data.password
        )
        user = existingCredential.user
        recoveredExistingAccount = true
      }

      const bootstrapResult = await ensureSignupBootstrap(user, data)

      if (recoveredExistingAccount) {
        if (bootstrapResult === 'recovered') {
          info('Recovered your account setup. Signing you in now.')
        } else {
          info('Account already existed. Signing you in now.')
        }
      }

      // Sign out and sign back in to fully initialize AuthContext
      await signOut(auth)
      await signIn(data.email, data.password)

      success(
        recoveredExistingAccount && bootstrapResult === 'recovered'
          ? 'Account recovered successfully!'
          : recoveredExistingAccount
            ? 'Signed in successfully!'
            : 'Account created successfully!'
      )

      navigate('/dashboard')
    } catch (err: unknown) {
      console.error('Signup error:', err)
      const error = err as { code?: string; message?: string }

      if (auth.currentUser) {
        try {
          await signOut(auth)
        } catch {
          // Best effort cleanup if bootstrap failed mid-flight.
        }
      }

      if (error.code === 'auth/email-already-in-use') {
        showError('This email is already registered. Use the same password to recover the account setup, or reset the password from Login.')
      } else if (error.code === 'auth/weak-password') {
        showError('Password is too weak. Please use a stronger password.')
      } else if (error.code === 'auth/invalid-email') {
        showError('Invalid email address.')
      } else if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        showError('This email is already registered. Use the original password or reset it from Login to finish signing in.')
      } else if (error.code === 'auth/operation-not-allowed') {
        showError('Account creation is currently disabled. Please try again later.')
      } else if (error.code === 'auth/too-many-requests') {
        showError('Too many attempts. Please try again later.')
      } else if (error.code === 'permission-denied') {
        showError('Account setup could not finish because Firestore permissions are still blocking the bootstrap documents.')
      } else {
        showError('Failed to create account. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gray-50 dark:bg-gray-900">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="flex justify-center mb-4">
            <Logo size="xl" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Start your 30-Day Free Trial</h1>
          <p className="text-gray-600 dark:text-gray-400">30 days free. No credit card required.</p>
        </div>

        {/* Form */}
        <Card padding="lg" theme="light">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Input
              theme="light"
              label="Agency Name"
              placeholder="Your Healthcare Agency"
              leftIcon={<Building2 className="h-5 w-5" />}
              error={errors.agencyName?.message}
              {...register('agencyName')}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                theme="light"
                label="First Name"
                placeholder="John"
                leftIcon={<User className="h-5 w-5" />}
                error={errors.firstName?.message}
                {...register('firstName')}
              />
              <Input
                theme="light"
                label="Last Name"
                placeholder="Doe"
                error={errors.lastName?.message}
                {...register('lastName')}
              />
            </div>

            <Input
              theme="light"
              label="Work Email"
              type="email"
              placeholder="you@agency.com"
              leftIcon={<Mail className="h-5 w-5" />}
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              theme="light"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a password"
              leftIcon={<Lock className="h-5 w-5" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              }
              hint="8+ characters, uppercase, lowercase, and number"
              error={errors.password?.message}
              {...register('password')}
            />

            <Input
              theme="light"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              leftIcon={<Lock className="h-5 w-5" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              }
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />

            <Button type="submit" isLoading={isLoading} className="w-full">
              Create Account
            </Button>

            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="text-primary-600 dark:text-primary-400 hover:underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-primary-600 dark:text-primary-400 hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </form>
        </Card>

        {/* Login link */}
        <p className="mt-6 text-center text-gray-600 dark:text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
